import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../api/client";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LockKey } from "@phosphor-icons/react";
import { BrandHero, BRAND } from "../components/Brand";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      nav("/", { replace: true });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#FAFAFA] flex" data-testid="login-page">
      {/* Left brand panel — DigiTrans blue → green gradient with orange triangle accent */}
      <div
        className="hidden lg:flex flex-1 flex-col justify-between p-14 text-white relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${BRAND.colors.blueDeep} 0%, ${BRAND.colors.blue} 45%, ${BRAND.colors.green} 100%)`,
        }}
      >
        {/* Soft grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        {/* Large decorative orange triangle */}
        <div
          aria-hidden
          className="absolute -bottom-16 -right-10 pointer-events-none opacity-90"
          style={{
            width: 0,
            height: 0,
            borderLeft: "140px solid transparent",
            borderRight: "140px solid transparent",
            borderBottom: `220px solid ${BRAND.colors.orange}`,
            transform: "rotate(24deg)",
            filter: "drop-shadow(0 12px 40px rgba(249,115,22,0.35))",
          }}
        />
        {/* Small decorative triangle top-right */}
        <div
          aria-hidden
          className="absolute top-24 right-16 pointer-events-none opacity-80"
          style={{
            width: 0,
            height: 0,
            borderLeft: "18px solid transparent",
            borderRight: "18px solid transparent",
            borderBottom: `28px solid ${BRAND.colors.orange}`,
            transform: "rotate(-30deg)",
          }}
        />

        <div className="relative z-10">
          <BrandHero />
        </div>

        <div className="relative z-10 space-y-4">
          <p
            className="text-xs uppercase tracking-[0.3em] text-white/80"
            style={{ fontFamily: "IBM Plex Mono, monospace" }}
          >
            IT delivery / phase 01
          </p>
          <h1
            className="text-5xl font-black leading-none tracking-tight"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Capture. Filter.
            <br />
            Ship on time.
          </h1>
          <p className="text-white/85 max-w-md text-sm leading-relaxed">
            A single source of truth for every deliverable, dev day, risk and sign-off — with full
            audit of who touched what, when.
          </p>
        </div>

        <div
          className="relative z-10 text-xs text-white/70 tracking-widest"
          style={{ fontFamily: "IBM Plex Mono, monospace" }}
        >
          v1.0 · INTERNAL USE ONLY
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md space-y-8" data-testid="login-form">
          {/* Compact brand mark on mobile (left panel is hidden < lg) */}
          <div className="lg:hidden flex items-center gap-3">
            <img
              src={BRAND.logoUrl}
              alt="DigiTrans Technology"
              className="h-10 w-auto object-contain"
              draggable={false}
            />
          </div>

          <div className="space-y-2">
            <div
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em]"
              style={{ color: BRAND.colors.blue }}
            >
              <LockKey size={14} weight="bold" /> Secure Sign In
            </div>
            <h2
              className="text-3xl font-black tracking-tight"
              style={{ color: BRAND.colors.ink, fontFamily: "Chivo, sans-serif" }}
            >
              Sign in to your workspace
            </h2>
            <p className="text-sm text-neutral-500">Use your credentials to continue.</p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-semibold">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-semibold">
                Password
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11"
              />
            </div>
          </div>

          {err ? (
            <div
              data-testid="login-error"
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
            >
              {err}
            </div>
          ) : null}

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full h-11 text-white rounded-md font-semibold text-sm tracking-wide transition-colors"
            style={{ backgroundColor: BRAND.colors.blue }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = BRAND.colors.blueDeep)}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = BRAND.colors.blue)}
          >
            {loading ? "Signing in..." : "Sign in →"}
          </Button>

          <p className="text-xs text-neutral-400 text-center">
            Enter your credentials to continue.
          </p>
        </form>
      </div>
    </div>
  );
}
